import { useEffect, useState } from 'react';
import { AppBar, Toolbar, Box, Drawer, Stack } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, Link } from 'react-router-dom';
import { useUser } from 'context/UserContext';
import { useSession } from '@baltlife/internal-sso-react';
import SearchBar from '../../SearchBar/SearchBar';
import { useNavigate } from 'react-router-dom';
import { AdminMenuItem } from '../Components/AdminMenuItem';
import { ProductToolkitMenuItem } from '../Components/ProductToolkitMenuItem';
import { MoreMenuItem } from '../Components/MoreMenuItem';
import { MobileNavButton } from '../Components/MobileNavButton';
import { NavButton } from '../Components/NavButton';
import { UserActions } from '../Components/UserActions';
import { UserMenu } from '../Components/UserMenu';
import { StaticHeaderContent } from '../Components/StaticHeaderContent';
import {
  appBarStyles,
  toolbarStyles,
  navigationMobileMenuStyles,
  navigationMenuStyles,
  navigationTopMenuSpan,
  navigationMiddleMenuSpan,
  navigationBottomMenuSpan,
} from '../Components/StyledComponents';
import { ApplicationsNavMenu } from '../../../components/ApplicationsNavMenu/ApplicationsNavMenu';
import IconButton from '@mui/material/IconButton';

const Logo = () => (
  <IconButton component={Link} to="/Home" sx={{ width: '150px', height: 'auto' }}>
    <img src={'/images/logo.png'} alt="Logo" style={{ width: '100%', padding: '.5rem' }} />
  </IconButton>
);

// Desktop Nav menu
const NavigationMenu = ({ isAuthenticated, user, location }) => {
  if (!isAuthenticated || !user.permissions || Object.keys(user.permissions).length === 0)
    return null;

  return (
    <Box component={'nav'} sx={navigationMenuStyles}>
      <ProductToolkitMenuItem LOB={user.LOB} />
      <NavButton to="/Forms" label="Forms" location={location} />
      <NavButton to="/Reports" label="Reports" location={location} />
      {user.isHO === false && (
        <NavButton
          to={user.userType === 'Agent' ? '/MyProfile/Demographic' : '/MyIMO'}
          label={user.userType === 'Agent' ? 'My Profile' : 'My IMO'}
          location={location}
        />
      )}
      {user.isHO === false && <NavButton to="/Policies" label="Policies" location={location} />}
      <NavButton to="/Training" label="Training" location={location} />
      {(user.division === 'CASG' || user.isHO) && (
        <NavButton to="/Marketing" label="Marketing" location={location} />
      )}
      <MoreMenuItem user={user} />
      <AdminMenuItem {...user.permissions} />
    </Box>
  );
};

const NavBar = () => {
  const user = useUser();
  const location = useLocation();
  const { isAuthenticated } = useSession();
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);

  const isMedium = useMediaQuery('(max-width:1200px)');
  const isMobile = useMediaQuery('(max-width:600px)');
  // The applications control carries a border, so it needs real space against the drawer
  // toggle rather than relying on the icon button's own padding to separate them.
  const appBtnStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '16px',
  };

  useEffect(() => {
    if (Object.keys(user.permissions).length === 0 && location.pathname !== '/Error/AccessDenied') {
      navigate('/Error/AccessDenied');
    }
  }, [location]);

  const toggleDrawer = () => {
    setOpenDrawer((prev) => !prev);
  };

  // Mobile Nav Menu
  const MenuItems = ({ toggleDrawer }) => {
    if (!isAuthenticated || !user.permissions || Object.keys(user.permissions).length === 0)
      return null;

    return (
      <Box component={'nav'} sx={navigationMobileMenuStyles}>
        <ProductToolkitMenuItem LOB={user.LOB} toggleDrawer={toggleDrawer} />
        <MobileNavButton
          toggleDrawer={toggleDrawer}
          to="/Forms"
          label="Forms"
          location={location}
        />
        <MobileNavButton
          toggleDrawer={toggleDrawer}
          to="/Reports"
          label="Reports"
          location={location}
        />
        {user.isHO === false && (
          <MobileNavButton
            toggleDrawer={toggleDrawer}
            to={user.userType === 'Agent' ? '/MyProfile/Demographic' : '/MyIMO/PendingReports'}
            label={user.userType === 'Agent' ? 'My Profile' : 'My IMO'}
            location={location}
          />
        )}
        <MobileNavButton
          toggleDrawer={toggleDrawer}
          to="/Policies"
          label="Policies"
          location={location}
        />
        <MobileNavButton
          toggleDrawer={toggleDrawer}
          to="/Training"
          label="Training"
          location={location}
        />
        {(user.division === 'CASG' || user.isHO) && (
          <MobileNavButton
            toggleDrawer={toggleDrawer}
            to="/Marketing"
            label="Marketing"
            location={location}
          />
        )}
        <MoreMenuItem user={user} toggleDrawer={toggleDrawer} />
        <AdminMenuItem {...user.permissions} toggleDrawer={toggleDrawer} />
      </Box>
    );
  };

  return (
    <AppBar position="sticky" sx={appBarStyles}>
      <Toolbar sx={toolbarStyles}>
        {isMobile ? (
          <>
            <Logo />
            <div style={appBtnStyle}>
              <ApplicationsNavMenu />
              <div onClick={toggleDrawer}>
                <div className={`menu-button-container${openDrawer ? ' active' : ''}`}>
                  <span className="top"></span>
                  <span className="middle"></span>
                  <span className="bottom"></span>
                </div>
              </div>
            </div>
            <Drawer
              anchor="left"
              open={openDrawer}
              onClose={toggleDrawer}
              PaperProps={{
                sx: {
                  width: '100%',
                  padding: '0px 16px',
                  justifyContent: 'space-between',
                },
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '88px',
                  }}
                >
                  <Logo />
                  <Box
                    sx={{
                      width: '30px',
                      height: '24px',
                      position: 'relative',
                      cursor: 'pointer',
                    }}
                    className={`${openDrawer ? 'active' : ''}`}
                    onClick={toggleDrawer}
                  >
                    <Box
                      component={'span'}
                      sx={{
                        ...navigationTopMenuSpan,
                        transform: openDrawer ? 'rotate(45deg)' : 'translateY(-8px)',
                      }}
                      className="top"
                    />
                    <Box
                      component={'span'}
                      sx={{
                        ...navigationMiddleMenuSpan,
                        opacity: openDrawer ? 0 : 1,
                      }}
                      className="middle"
                    />
                    <Box
                      component={'span'}
                      sx={{
                        ...navigationBottomMenuSpan,
                        transform: openDrawer ? 'rotate(-45deg)' : 'translateY(8px)',
                      }}
                      className="bottom"
                    />
                  </Box>
                </Box>
                {isAuthenticated && <SearchBar fullWidth />}
                <MenuItems toggleDrawer={toggleDrawer} />
              </Box>
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ padding: '1rem 0', lineHeight: 'initial' }}
                >
                  <UserMenu />
                </Stack>
                <StaticHeaderContent isMobile={isMobile} />
              </Box>
            </Drawer>
          </>
        ) : isMedium ? (
          <>
            <Logo />
            <div style={appBtnStyle}>
              <ApplicationsNavMenu />
              <div onClick={toggleDrawer}>
                <div className={`menu-button-container${openDrawer ? ' active' : ''}`}>
                  <span className="top"></span>
                  <span className="middle"></span>
                  <span className="bottom"></span>
                </div>
              </div>
            </div>
            <Drawer
              anchor="left"
              open={openDrawer}
              onClose={toggleDrawer}
              PaperProps={{
                sx: {
                  width: '40%',
                  padding: '0px 16px',
                  justifyContent: 'space-between',
                },
              }}
            >
              <Box>
                {isAuthenticated && <SearchBar fullWidth />}
                <MenuItems toggleDrawer={toggleDrawer} />
              </Box>
              <Stack direction="row" spacing={1} sx={{ padding: '1rem 0', lineHeight: 'initial' }}>
                <UserMenu />
              </Stack>
            </Drawer>
          </>
        ) : (
          <>
            <Logo />
            <NavigationMenu isAuthenticated={isAuthenticated} user={user} location={location} />
            <UserActions isAuthenticated={isAuthenticated} />
          </>
        )}
      </Toolbar>
      {!isMobile && <StaticHeaderContent />}
    </AppBar>
  );
};

export default NavBar;
